import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";
import { useNotificationSlot } from "@/components/system/NotificationCoordinator";

// Browser hook for PWA install. `beforeinstallprompt` fires on
// Chromium-based browsers when the PWA criteria are met (HTTPS,
// service worker, valid manifest). We capture the event, show our
// own UI, then call `.prompt()` when the user accepts.
//
// Safari iOS doesn't fire this event — there's no programmatic
// install. We could show an "Add to Home Screen" hint specifically
// for iOS Safari, but that's noise on top of the already-cluttered
// first-paint experience. Skipped.

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "pwa_install_dismissed_at";
const VISIT_COUNT_KEY = "pwa_install_visit_count";
const COOKIE_CONSENT_KEY = "cookie_consent";
// Dismissed banner stays hidden for 30 days so we don't nag.
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
// Don't show on the first couple of sessions — gives the user time
// to actually decide if they like the app before we ask them to
// install it.
const MIN_VISITS = 3;

// Routes where we suppress the prompt to avoid covering CTAs / forms.
const SUPPRESS_ROUTES = new Set<string>(["/login", "/signup"]);

export function InstallPrompt() {
  const { t } = useTranslation();
  const location = useLocation();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [wantsToShow, setWantsToShow] = useState(false);
  const visitCountedRef = useRef(false);

  // Bump visit counter once per mount. Used to gate the prompt so we
  // don't ask brand-new users to install before they've explored.
  useEffect(() => {
    if (visitCountedRef.current) return;
    visitCountedRef.current = true;
    try {
      const raw = localStorage.getItem(VISIT_COUNT_KEY);
      const current = raw ? parseInt(raw, 10) || 0 : 0;
      localStorage.setItem(VISIT_COUNT_KEY, String(current + 1));
    } catch {
      // Private mode — ignore
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      // Prevent the default mini-infobar on Android
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);

      try {
        // Gate 1: cookie consent has been answered (don't pile up modals)
        const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
        if (!consent) return;

        // Gate 2: at least N visits
        const visits = parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10);
        if (isNaN(visits) || visits < MIN_VISITS) return;

        // Gate 3: not within dismiss cooldown
        const last = localStorage.getItem(DISMISS_KEY);
        if (last) {
          const lastMs = parseInt(last, 10);
          if (!isNaN(lastMs) && Date.now() - lastMs < DISMISS_DURATION_MS) {
            return;
          }
        }
      } catch {
        // Private mode — show anyway
      }
      setWantsToShow(true);
    };

    const installed = () => {
      // User completed install via OS dialog — hide our UI
      setWantsToShow(false);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installed);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const suppressed = SUPPRESS_ROUTES.has(location.pathname);
  const visible = useNotificationSlot(
    "install",
    wantsToShow && !!deferred && !suppressed,
  );

  if (!visible || !deferred) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch { /* private mode */ }
    setWantsToShow(false);
  };

  const handleInstall = async () => {
    if (!deferred) return;
    try {
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "dismissed") {
        handleDismiss();
      } else {
        setWantsToShow(false);
      }
      setDeferred(null);
    } catch {
      handleDismiss();
    }
  };

  return (
    <div
      role="region"
      aria-label={t("install.title")}
      className="fixed bottom-20 md:bottom-4 right-4 z-40 max-w-xs animate-fade-in"
    >
      <div className="rounded-2xl border border-line/40 bg-background/95 backdrop-blur-xl shadow-lg p-4 relative">
        <button
          type="button"
          onClick={handleDismiss}
          className="tap-target absolute top-2 right-2 p-2 rounded-md hover:bg-muted/40 text-muted"
          aria-label={t("install.dismiss")}
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-start gap-3 mb-3 pr-6">
          <div className="w-10 h-10 rounded-lg bg-brand/10 grid place-items-center flex-shrink-0">
            <Download className="w-5 h-5 text-brand" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-text">{t("install.title")}</p>
            <p className="text-xs text-muted">{t("install.body")}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          className="w-full px-3 py-2 rounded-lg bg-brand text-white text-sm font-medium hover:brightness-105"
        >
          {t("install.cta")}
        </button>
      </div>
    </div>
  );
}
